import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { NumberLiteralType } from 'typescript';
import { ChildProfile } from './appStateSlice';
import { OptionsAndGoProps } from '../../component/actions/optionsAndGo';
import { PauseAndGoProps } from '../../component/actions/pauseAndGo';
import { DragAndDropProps } from '../../component/miniGames/dragAndDrop/dragAndDrop';
import { MatchingCardsProps } from '../../component/miniGames/matchingCards/matchingCards';
import { PuzzleProps } from '../../component/miniGames/puzzle/puzzle';
import { QuizProps } from '../../component/miniGames/quiz/quiz';
import { SwipingCardsProps } from '../../component/miniGames/swipingCards/swipingCards';
import type { RootState } from '../store';

// Interface

export interface Course {
  id: string;
  title: string;
  grade: 'g1' | 'g3';
  subject:
    | 'chemistry'
    | 'biology'
    | 'environmental'
    | 'physics'
    | 'engineering'
    | 'astronomy and earth science';
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  topics?: any;
}
export interface Topic {
  id: string;
  title: string;
  isEnabled: boolean;
  topicDetail: {
    thumbnail?: string;
    sequence?: number;
  };
  course: Course;
  createdAt: string;
  updatedAt: string;
}

export interface Lesson {
  id: string;
  title: string;
  type: 'video' | 'checkpoint';
  lessonDetail: {
    lessonIcon: 'normal' | 'experiment' | 'quiz';
    lessonNuts: number;
    lessonSequence: number;
    lessonSubtitle: string;
    lessonThumbnail: string;
  };
}

interface VideoChapter {
  name: string;
  mainStream: boolean;
  description: string;
  startTimestamp: number;
}

interface VideoInfo {
  id: string;
  videoDetail: {
    src: string;
    file: string;
    chapters: VideoChapter[];
  };
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export type MultiLanguageVoConfiguration = {
  eng: string | undefined;
  chi: string | undefined;
  pth: string | undefined;
};

export type MinigameInteractionConfigBase<T> = T & {
  maxNut: number;
  gameName: 'match-card' | 'puzzles' | 'swiping-card' | 'drag-and-drop';
  backgroundMusic: string | undefined;
  instructionVO: MultiLanguageVoConfiguration;
  triggerChapter: number;
};

export type ActionInteractionConfigBase<T> = T & {
  actionName: 'pause-and-go' | 'option-and-go';
  triggerChapter: number;
};

export type MinigameInteractionConfig = MinigameInteractionConfigBase<
  | MatchingCardsProps
  | SwipingCardsProps
  | DragAndDropProps
  | PuzzleProps
  | QuizProps
>;

export type ActionInteractionConfig = ActionInteractionConfigBase<
  PauseAndGoProps | OptionsAndGoProps
>;

export type QuizConfig = QuizProps & {
  triggerChapter: number;
};

export interface Interaction {
  id: string;
  video: VideoInfo;
  interaction: {
    id: string;
    type: 'action' | 'minigame' | 'quiz';
    interactionConfig:
      | MinigameInteractionConfig
      | ActionInteractionConfig
      | QuizConfig;
  };
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface Video {
  video: VideoInfo;
  interaction: Interaction[];
}

export interface TopicProgressEntry {
  topicId: string;
}
export interface LessonProgressEntry {
  lessonId: string;
  nutsGained: number;
  maxNut: number;
  chapter?: number;
  createdAt?: string;
}
export interface ChildProgress {
  id: string;
  userId: string;
  childId: string;
  progressLevel: 'course' | 'topic' | 'lesson';
  progressTarget: string;
  progressDetail: {
    finished?: LessonProgressEntry[] | TopicProgressEntry[];
    current?: LessonProgressEntry | TopicProgressEntry;
  };
  createdAt?: string;
  updatedAt?: string;
}

export type FullCurriculum = Array<
  {
    lessons: Lesson[];
    topics: Array<
      {
        lessons: Lesson[];
      } & Topic
    >;
  } & Course
>;

export type ChildFullProgress = {
  course: ChildProgress[];
  topic: ChildProgress[];
  lesson: ChildProgress[];
  accumulatedNuts: number;
};
type EventType = 'program-included' | 'standalone';
type EventDetail = ProgramEventDetail | StandaloneEventDetail;
type ProgramEventDetail = {
  programId: Event['id'];
};
type StandaloneEventDetail = {
  empty: boolean;
};

export interface Event {
  id: string;

  title: string;

  type: EventType;

  eventDetail: EventDetail;

  venue: string;

  startedAt: Date;

  endedAt: Date;

  createdAt: Date;
  updatedAt: Date;
}

// Define a type for the slice state
export interface AppInterfaceDataSlice {
  childProfile?: ChildProfile;
  course: Course[] | undefined;
  selectedCourse: Course | undefined;
  courseProgress: any;
  topic: Topic[] | undefined;
  selectedTopic: Topic | undefined;
  topicProgress: ChildProgress | undefined;
  lesson: Lesson[] | undefined;
  selectedLesson: Lesson | undefined;
  lessonProgress: LessonProgressEntry | undefined;
  video: Video | undefined;
  fullCurriculum: FullCurriculum | undefined;
  childFullProgress: ChildFullProgress | undefined;
  selectedGrade: Course['grade'] | undefined;
  programEvents: Event[] | undefined;
  fullCurriculumByLesson: unknown;
  childFullProgressUniqueByLesson: unknown;
}

// Define the initial state using that type
const initialState: AppInterfaceDataSlice = {
  childProfile: undefined,
  course: undefined,
  selectedCourse: undefined,
  courseProgress: undefined,
  topic: undefined,
  selectedTopic: undefined,
  topicProgress: undefined,
  lesson: undefined,
  selectedLesson: undefined,
  lessonProgress: undefined,
  video: undefined,
  fullCurriculum: undefined,
  childFullProgress: undefined,
  selectedGrade: undefined,
  programEvents: undefined,
  fullCurriculumByLesson: undefined,
  childFullProgressUniqueByLesson: undefined,
};

export const appInterfaceDataSliceSlice = createSlice({
  name: 'appInterfaceData',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setAppInterfaceData(
      state,
      action: PayloadAction<{
        dataKey: keyof AppInterfaceDataSlice;
        data: unknown;
      }>,
    ): void {
      state[action.payload.dataKey] = action.payload.data;
    },
  },
});

export const { setAppInterfaceData } = appInterfaceDataSliceSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectAppInterfaceData = (
  state: RootState,
): AppInterfaceDataSlice => {
  return state.appInterfaceData;
};

export default appInterfaceDataSliceSlice.reducer;
